import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { InfiniteResult } from 'src/app/_models/body';
import { Global } from 'src/app/_services/global.service';
import { ModalService } from 'src/app/_services/modal.service';
import { VoiceService } from 'src/app/_services/voice.service';
import { BaseComponent } from 'src/app/ui-library/component/base.component';
import { ConfirmComponent } from 'src/app/ui-library/dialog/confirm/confirm.component';
import { VoiceCloneTemplate } from './voice-item.constant';

@Component({
  selector: 'app-voice',
  templateUrl: './voice.component.html',
  styleUrls: ['./voice.component.css']
})
export class VoiceComponent extends BaseComponent {
  columnDefs: any[] = [];
  data: Array<any> = [];
  formGroup!: FormGroup;
  rowClassRules: any;
  private readonly speakerNamePattern = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
  private audioPlayer: HTMLAudioElement | null = null;
  private playingAudioUrl: string | null = null;
  private langCodeOptions: string[] = this.global.section.VoiceCloneLangCodeSelections || [];
  constructor(
    private voiceService: VoiceService,
    public global: Global,
    private modalService: ModalService,
    private ngbModal: NgbModal,
    private datePipe: DatePipe,
    private toastr: ToastrService,
  ) {
    super(global);

  }

  ngOnInit(): void {
    this.rowClassRules = {
      'row-missing-azure-profile': (params: any) => !params?.data?.azureSpeakerProfileId,
    };
    this.setColumnDefs()
  }

  setColumnDefs(): void {
    this.columnDefs = [
      { headerName: 'ID', field: 'id' },
      { headerName: 'Speaker Name', field: "speakerName" },
      { headerName: 'Voice ID', field: 'voiceId' },
      { headerName: 'Azure Speaker Profile ID', field: 'azureSpeakerProfileId' },
      {
        headerName: 'Azure Speaker Demo',
        field: 'azureSpeakerDemoUrl',
        minWidth: 170,
        cellRenderer: (params: any) => {
          return params.value
            ? `<i class="fas fa-fw fa-play text-primary" id="playDemoAudio" title="Play Demo Audio" style="cursor: pointer;"></i>`
            : '';
        },
        onCellClicked: (params: any) => {
          if (params.event.target.id === 'playDemoAudio') {
            this.playAudio(params?.data?.azureSpeakerDemoUrl, 'demo');
          }
        },
      },
      {
        headerName: 'Consent Audio',
        field: 'azureConsentUrl',
        minWidth: 150,
        cellRenderer: (params: any) => {
          return params.value
            ? `<i class="fas fa-fw fa-volume-up text-info" id="playConsentAudio" title="Play Consent Audio" style="cursor: pointer;"></i>`
            : '';
        },
        onCellClicked: (params: any) => {
          if (params.event.target.id === 'playConsentAudio') {
            this.playAudio(params?.data?.azureConsentUrl, 'consent');
          }
        },
      },
      {
        headerName: 'Created At', field: 'createdAt', valueFormatter: (params: any) => {
          return this.datePipe.transform(params.value, 'short')
        }
      },
      {
        headerName: 'Action',
        pinned: 'right',
        minWidth: 170,
        cellRenderer: (params: any) => {
          const retryButton = !params?.data?.azureSpeakerProfileId
            ? `<i class="fas fa-fw fa-redo text-warning" id="retry" title="Retry" style="margin-right:3px; cursor: pointer;"></i>`
            : '';

          return ` <i class="fas fa-fw fa-bars" id="moreDetail" style="margin-right:3px; cursor: pointer;"></i>
                    ${retryButton}
                    <i class="fas fa-fw fa-trash grid-btn-icon text-danger" id="delete" style="margin-right:3px; cursor: pointer;"></i>`;

        },
        onCellClicked: (params: any) => {
          switch (params.event.target.id) {
            case 'moreDetail':
              this.modalService.showDetail(params.data, 'Detail')
              break;
            case 'retry':
              this.showItem(params?.data, true)
              break;
            case 'edit':
              this.showItem(params?.data)
              break;
            case 'delete':
              this.modalService.createNzDeleteModal(() => this.delete.call(this, params?.data?.id))
              break;
          }
        },
      },
    ];

  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
    this.getAll();
  }

  getAll() {
    this.voiceService.getAll().subscribe((res: InfiniteResult) => {
      this.data = res.data
    })
  }


  private speakerNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      if (this.speakerNamePattern.test(value)) {
        return null;
      }
      return {
        pattern: {
          message: 'Only letters and single spaces are allowed. No leading or trailing spaces.'
        }
      };
    };
  }

  genFG(orgData: any, isRetry?: boolean): void {
    this.formGroup = new FormGroup({
      speakerName: new FormControl({
        value: orgData?.speakerName,
        disabled: !!isRetry,
      }, [Validators.required, this.speakerNameValidator()]),
      langCode: new FormControl(orgData?.langCode || this.langCodeOptions[0], Validators.required),
      files: new FormControl(orgData?.files, Validators.required)
    });
  }

  playAudio(audioUrl: string, audioType: 'demo' | 'consent') {
    if (!audioUrl) {
      this.toastr.warning(`No ${audioType} audio url found.`, 'Warning');
      return;
    }

    if (this.audioPlayer && this.playingAudioUrl === audioUrl && !this.audioPlayer.paused) {
      this.audioPlayer.pause();
      this.audioPlayer.currentTime = 0;
      this.playingAudioUrl = null;
      return;
    }

    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer.currentTime = 0;
    }

    this.audioPlayer = new Audio(audioUrl);
    this.playingAudioUrl = audioUrl;
    this.audioPlayer.onended = () => {
      this.playingAudioUrl = null;
    };
    this.audioPlayer.play().catch(() => {
      this.toastr.error(`Failed to play ${audioType} audio.`, 'Error');
      this.playingAudioUrl = null;
    });
  }

  showItem(data?: any, isRetry?: boolean): void {
    this.genFG(data, isRetry);
    const dialogTemplate = VoiceCloneTemplate(this.langCodeOptions);
    this.modalService.createNzModal(() => this.onSubmit.call(this), this.formGroup, dialogTemplate, false);
  }


  onSubmit() {
    return new Observable(subscriber => {
      const speakerName = this.formGroup.get('speakerName')?.value;
      const files = this.formGroup.get('files')?.value;

      const isSpeakerNameMissing = speakerName === null || speakerName === undefined || speakerName === '';
      const isFilesMissing = files === null || files === undefined || files === '' || (Array.isArray(files) && files.length === 0);

      if (isSpeakerNameMissing || isFilesMissing) {
        this.formGroup.get('speakerName')?.markAsTouched();
        this.formGroup.get('files')?.markAsTouched();
        this.toastr.warning('Please fill in speakerName and files before submit.', 'Validation');
        subscriber.complete();
        return;
      }

      const saveSuccess = () => {
        this.getAll()
        this.toastr.success('Data Saved Successfully...!', 'Success');
        subscriber.next();
        subscriber.complete();
      };

      const inputSpeakerName = speakerName;

      const submitVoice = () => {
        const rawValue = this.formGroup.getRawValue();
        const formData = {
          ...rawValue,
          speakerName: inputSpeakerName,
        };
        const request = this.voiceService.addVoice(formData);
        request.subscribe({
          next: () => saveSuccess(),
          error: (error) => {
            this.getAll();
            subscriber.error(error)
          }
        });
      };

      const isDuplicatedSpeakerName = inputSpeakerName
        ? this.data.some((item: any) => item?.speakerName === inputSpeakerName)
        : false;

      if (isDuplicatedSpeakerName) {
        const modal = this.ngbModal.open(ConfirmComponent, { size: 'sm' });
        modal.componentInstance.confirmMessage = `Speaker name \"${inputSpeakerName}\" already exists. This operation will overwrite the original data. Continue anyway?`;
        modal.result.then((res: any) => {
          if (!res) {
            subscriber.complete();
            return;
          }
          submitVoice();
        }).catch(() => {
          subscriber.complete();
        });
        return;
      }

      submitVoice();
    });
  }

  delete(id: any): void {
    this.voiceService.deleteVoice(id).subscribe(res => {
      this.getAll();
      this.toastr.success('Data Delete Successfully...!', 'Success');
    })
  }

  ngOnDestroy() {
    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer.currentTime = 0;
    }
    super.ngOnDestroy();
  }
}